import { extend } from 'flarum/common/extend';
import styleSelectedText from 'flarum/common/utils/styleSelectedText';
import TextColorDropdown from './components/TextColorDropdown';
import { COLOR_VALUE } from './utils/textColors';
import textColorMarkdownIt from './utils/textColorMarkdownIt';

// What's used here of fof/rich-text (its own modules, a lazy chunk,
// untyped from here) and of the Tiptap editor behind it.

const MARK = 'text_color';
let textColorMark;

/**
 * The colour as a mark of its own, kept as [color=…]…[/color] - what
 * flarum/bbcode's COLOR renders, the phpBB posts' colours included.
 * Pasted from elsewhere, a text keeps its colour only if it was this
 * editor's (data-color): a web page's, a word processor's (their dark
 * greys, their link blues) would come out as [color] tags everywhere.
 */
function createTextColorMark() {
  const tiptap = window.flarum.reg.get('fof-rich-text', 'common/tiptap/tiptap');
  if (!tiptap) return null;
  return tiptap.Mark.create({
    name: MARK,
    // Ranked first in the schema, outermost when serialised:
    // "[color=…][a link](…)[/color]" - inside the link's text, its "[" read
    // back as a link nested in a link, which markdown-it refuses: the link
    // shown raw once edited. Tiptap's Link: 1000.
    priority: 1100,
    addAttributes() {
      return {
        color: {
          default: null,
          parseHTML: element => element.getAttribute('data-color'),
          renderHTML: attributes => ({
            'data-color': attributes.color,
            style: `color: ${attributes.color}`
          })
        }
      };
    },
    parseHTML() {
      // false: not a match; null: a match, its attributes read above.
      return [{
        tag: 'span[data-color]',
        getAttrs: element => COLOR_VALUE.test(element.getAttribute('data-color') ?? '') ? null : false
      }];
    },
    renderHTML(_ref) {
      let {
        HTMLAttributes
      } = _ref;
      return ['span', HTMLAttributes, 0];
    }
  });
}

/**
 * A text colour button in the composer's toolbar (Jplm, 2026-10-19: "On
 * n'a plus la fonction couleur de texte ?"), as phpBB had - its palette
 * the colours most used there (utils/textColors.ts) -, writing the same
 * [color=…]…[/color] flarum/bbcode renders: in the rich editor (most
 * members'), the selection, or what's typed next, coloured as with bold;
 * in the plain one, the selection wrapped in those tags.
 *
 * fof/rich-text's modules by their ext: path, not imported - see
 * removeRichTextToolbarButtons.ts: a lazy chunk, loaded with the editor.
 */
export default function addTextColorButton() {
  extend('ext:fof/rich-text/common/tiptap/TiptapEditorDriver', 'buildExtensions', function (items) {
    textColorMark ??= createTextColorMark();
    if (textColorMark) items.add('champisTextColor', textColorMark);
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder', 'buildTokenizer', function (md) {
    md.use(textColorMarkdownIt);
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder', 'buildTokens', function (tokens) {
    tokens[MARK] = {
      mark: MARK,
      getAttrs: token => ({
        color: token.attrGet('color')
      })
    };
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownSerializerBuilder', 'buildMarks', function (marks) {
    marks[MARK] = {
      open: (_state, mark) => `[color=${mark.attrs.color}]`,
      close: '[/color]',
      mixable: true,
      expelEnclosingWhitespace: true
    };
  });

  // Right after strikethrough, with the other text styles.
  extend('ext:fof/rich-text/common/components/TiptapMenu', 'items', function (items) {
    const editor = this.attrs.editor;
    const color = editor.getAttributes(MARK).color;
    items.add('champisTextColor', m(TextColorDropdown, {
      current: typeof color === 'string' ? color : null,
      withDefault: true,
      onpick: picked => {
        const chain = editor.chain().focus();
        (picked ? chain.setMark(MARK, {
          color: picked
        }) : chain.unsetMark(MARK)).run();
      }
    }), 72);
  });

  // flarum-markdown's own method, not in core's TextEditor typings.
  extend('flarum/common/components/TextEditor', 'markdownToolbarItems', function (items) {
    items.add('champisTextColor', m(TextColorDropdown, {
      onpick: picked => {
        if (!picked) return;
        const editor = this.attrs.composer.editor;

        // Its other fields defaulted by styleSelectedText() itself, as flarum-markdown has them.
        styleSelectedText(editor.el, {
          prefix: `[color=${picked}]`,
          suffix: '[/color]',
          trimFirst: true
        });
      }
    }), 650);
  });
}